import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { DateTimePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { time } from '~/utils';

import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
import { Moment } from '~/types';
import { resource } from '../IdentityAuditLog';
import { ApiIdentity, ApiIdentityFilters } from '~/types/apiIdentity';
import { hasCurrentLocale12HourFormat } from '~/utils/moment';

interface FiltersProps {
  record?: ApiIdentity;
}

const Filters = (props: FiltersProps) => {
  const { record } = props;
  const filters = useSelector(selectors.getFilters<ApiIdentityFilters>(resource));

  const [localValues, setLocalValues] = useState(filters);
  const classes = useStyles();
  const dispatch = useDispatch();
  const translate = useTranslate();

  const translateDateTime = (string: string) =>
    translate(`${i18nFilters}.KeyboardDateTimePicker.${string}`);

  const dispatchFilterChange = (name: string, value: string | Moment) => {
    dispatch(filterChange({ resource, filters: { [name]: value || null } }));
  };

  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 1000), []);

  const handleDateTimeChange = (data: Moment, inputType: string) => {
    debouncedDispatchFilterChange(inputType, data);
  };

  // const handleInput = (e: any) => {
  //   const { name, value } = e.target;
  //   dispatchFilterChange(name, value);
  // };

  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  useEffect(() => {
    dispatch(
      filterChange({
        resource,
        filters: {
          fromTimestamp: time().subtract(1, 'months'),
          toTimestamp: time(),
        },
      })
    );
    return () => {
      dispatch(cleanupFilters({ resource }));
    };
  }, [dispatch, record]);

  return (
    <Fragment>
      {filters && (
        <div className={classes.container}>
          <div className={classes.inputsGroup}>
            <DateTimePicker
              className={classes.datePickers}
              format='L - LT'
              ampm={hasCurrentLocale12HourFormat()}
              margin='normal'
              label={translateDateTime('from')}
              value={localValues.fromTimestamp}
              onChange={(date) => handleDateTimeChange(date as Moment, 'fromTimestamp')}
            />
            <DateTimePicker
              className={classes.datePickers}
              format='L - LT'
              ampm={hasCurrentLocale12HourFormat()}
              margin='normal'
              label={translateDateTime('to')}
              value={localValues.toTimestamp}
              onChange={(date) => handleDateTimeChange(date as Moment, 'toTimestamp')}
            />
          </div>
        </div>
      )}
    </Fragment>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingBottom: theme.spacing(1),
  },
  inputsGroup: {
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
  },
  formControl: {
    marginRight: theme.spacing(1),
    width: '100%',
    maxWidth: 180,
    '&:last-child': {
      marginRight: 0,
    },
  },
  datePickers: {
    marginRight: theme.spacing(1),
    marginTop: 0,
    marginBottom: 0,
    minWidth: 170,
    maxWidth: 180,
    flexGrow: 1,
    '& > div': {
      paddingRight: 0,
      '& > div': {
        marginLeft: 0,
      },
    },
  },
}));

const i18nFilters = 'components.ApiIdentity.Filters';

export default Filters;
